import { useState, useEffect } from 'react';
import type { User, UserCredential } from 'firebase/auth';
import * as authService from '@/services/auth.service';

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  register: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  loginWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
}

/**
 * Custom hook for authentication state management
 * Wraps Firebase Auth service and provides React state
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChanged((authUser) => {
      setUser(authUser);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const register = async (email: string, password: string): Promise<UserCredential> => {
    return authService.register(email, password);
  };

  const login = async (email: string, password: string): Promise<UserCredential> => {
    return authService.login(email, password);
  };

  const loginWithGoogle = async (): Promise<UserCredential> => {
    return authService.loginWithGoogle();
  };

  const logout = async (): Promise<void> => {
    return authService.logout();
  };

  return {
    user,
    loading,
    register,
    login,
    loginWithGoogle,
    logout,
  };
}
